import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import PermissionsMatrix from '@/components/PermissionsMatrix';
import { useToast } from '@/components/ToastProvider';
import { ShieldCheck, Users, FolderKanban, Clock, ClipboardCheck, Plus, Pencil, KeyRound, Eye, Lock, Settings } from 'lucide-react';

interface DashboardStats { users: number; projects: number; totalHours: number; pendingApprovals: number; }

export default function AdminPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [sLoading, setSLoading] = useState(true);
  const [logs, setLogs] = useState<any[]>([]);
  const [lLoading, setLLoading] = useState(true);
  const [userList, setUserList] = useState<any[]>([]);
  const [uLoading, setULoading] = useState(true);

  // Create user
  const [createOpen, setCreateOpen] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'ADMIN' | 'PM' | 'USER'>('USER');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit user
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<any | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<'ADMIN' | 'PM' | 'USER'>('USER');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');

  // Password reset
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState<any | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const { add: toast } = useToast();

  // Config / registration toggle
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);

  async function loadConfig() {
    setConfigLoading(true);
    try {
      const d = await api<{ registrationEnabled: boolean }>('/config');
      setRegistrationEnabled(d.registrationEnabled ?? true);
    } catch {}
    setConfigLoading(false);
  }

  async function handleToggleRegistration(enabled: boolean) {
    setConfigSaving(true);
    try {
      await api('/config', { method: 'PUT', body: JSON.stringify({ registrationEnabled: enabled }) });
      setRegistrationEnabled(enabled);
      toast({
        title: enabled ? 'Public registration enabled' : 'Public registration disabled',
        description: enabled ? 'New users can now register publicly.' : 'New public registrations are now blocked.',
        variant: 'default',
      });
    } catch (err: any) {
      toast({
        title: 'Failed to update setting',
        description: err.message || 'Could not save registration toggle. Please try again.',
        variant: 'destructive',
      });
    }
    setConfigSaving(false);
  }

  async function handleSeedPermissions() {
    try {
      await api('/permissions/seed', { method: 'POST' });
      toast({ title: 'Permissions seeded', description: 'Default permissions restored successfully.', variant: 'default' });
    } catch (err: any) {
      toast({ title: 'Seed failed', description: err.message || 'Failed to seed permissions.', variant: 'destructive' });
    }
  }

  async function loadStats() {
    setSLoading(true);
    try { const d = await api<DashboardStats>('/admin/dashboard'); setStats(d); } catch {}
    setSLoading(false);
  }
  async function loadLogs() {
    setLLoading(true);
    try { const d = await api<any[]>('/admin/audit-logs'); setLogs(d || []); } catch {}
    setLLoading(false);
  }
  async function loadUsers() {
    setULoading(true);
    try { const d = await api<any[]>('/users'); setUserList(d || []); } catch {}
    setULoading(false);
  }

  useEffect(() => { loadStats(); loadLogs(); loadUsers(); loadConfig(); }, []);

  const handleImpersonate = (userId: string) => { localStorage.setItem('jani_impersonate_user_id', userId); window.location.reload(); };

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(''); setCreateBusy(true);
    try {
      await api('/users', { method: 'POST', body: JSON.stringify({ name: newUserName, email: newUserEmail, globalRole: newUserRole, password: newUserPassword }) });
      setNewUserName(''); setNewUserEmail(''); setNewUserPassword(''); setCreateOpen(false); loadUsers();
    } catch (err: any) { setCreateError(err.message); }
    finally { setCreateBusy(false); }
  }

  async function handleEditUser(e: React.FormEvent) {
    e.preventDefault();
    if (!editUser) return;
    setEditError(''); setEditBusy(true);
    try {
      await api(`/users/${editUser.id}`, { method: 'PATCH', body: JSON.stringify({ name: editName, email: editEmail, globalRole: editRole }) });
      setEditOpen(false); setEditUser(null); loadUsers();
    } catch (err: any) { setEditError(err.message); }
    finally { setEditBusy(false); }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!resetUser) return;
    setResetBusy(true);
    try {
      await api(`/users/${resetUser.id}/reset-password`, { method: 'POST', body: JSON.stringify({ password: newPassword }) });
      setNewPassword(''); setResetOpen(false); setResetUser(null);
    } catch {}
    finally { setResetBusy(false); }
  }

  const userColumns = [
    { key: 'name', header: 'Name', render: (u: any) => (
      <div className="flex items-center gap-2">
        <Avatar className="h-6 w-6"><AvatarFallback className="text-[10px] bg-[var(--color-primary)] text-white">{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
        <span className="font-medium text-sm">{u.name}</span>
      </div>
    )},
    { key: 'email', header: 'Email', render: (u: any) => <span className="text-xs text-[var(--color-muted-foreground)]">{u.email}</span> },
    { key: 'role', header: 'Role', render: (u: any) => <Badge variant={u.globalRole === 'ADMIN' ? 'destructive' : 'secondary'}>{u.globalRole}</Badge> },
    { key: 'actions', header: '', className: 'w-32', render: (u: any) => (
      <div className="flex items-center gap-1">
        <Button data-testid="edit-user-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)]" onClick={() => { setEditUser(u); setEditName(u.name); setEditEmail(u.email); setEditRole(u.globalRole); setEditOpen(true); }}><Pencil className="h-4 w-4" /></Button>
        <Button data-testid="reset-password-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)]" onClick={() => { setResetUser(u); setResetOpen(true); }}><KeyRound className="h-4 w-4" /></Button>
        <Button data-testid="impersonate-button" variant="ghost" size="sm" className="h-8 text-[var(--color-primary)]" onClick={() => handleImpersonate(u.id)}><Eye className="mr-1 h-3.5 w-3.5" /> View as</Button>
      </div>
    )},
  ];

  return (
    <div className="animate-in space-y-6">
      <PageHeader title={t('admin') || 'Admin'} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Users</CardTitle><Users className="h-4 w-4 text-[var(--color-muted-foreground)]" /></CardHeader><CardContent>{sLoading ? <Skeleton className="h-8 w-16" /> : <div className="text-3xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{stats?.users ?? 0}</div>}</CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Projects</CardTitle><FolderKanban className="h-4 w-4 text-[var(--color-muted-foreground)]" /></CardHeader><CardContent>{sLoading ? <Skeleton className="h-8 w-16" /> : <div className="text-3xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{stats?.projects ?? 0}</div>}</CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Total Hours</CardTitle><Clock className="h-4 w-4 text-[var(--color-muted-foreground)]" /></CardHeader><CardContent>{sLoading ? <Skeleton className="h-8 w-16" /> : <div className="text-3xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{(stats?.totalHours ?? 0).toFixed(0)}</div>}</CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-[var(--color-muted-foreground)]">Pending Approvals</CardTitle><ClipboardCheck className="h-4 w-4 text-[var(--color-muted-foreground)]" /></CardHeader><CardContent>{sLoading ? <Skeleton className="h-8 w-16" /> : <div className="text-3xl font-bold text-[var(--color-primary)] font-mono tracking-tight">{stats?.pendingApprovals ?? 0}</div>}</CardContent></Card>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger data-testid="users-tab" value="users">Users</TabsTrigger>
          <TabsTrigger data-testid="permissions-tab" value="permissions">Permissions</TabsTrigger>
          <TabsTrigger data-testid="security-tab" value="security">Security</TabsTrigger>
          <TabsTrigger data-testid="audit-tab" value="audit">Audit Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <PageHeader title="" description="">
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild><Button data-testid="create-user-button" size="sm"><Plus className="mr-2 h-4 w-4" /> New User</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New User</DialogTitle><DialogDescription>Create a new user account.</DialogDescription></DialogHeader>
                <form id="create-user-form" onSubmit={handleCreateUser} className="space-y-4 py-2">
                  {createError && <Badge variant="destructive">{createError}</Badge>}
                  <div className="space-y-2"><Label>Name</Label><Input value={newUserName} onChange={e=>setNewUserName(e.target.value)} required /></div>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" value={newUserEmail} onChange={e=>setNewUserEmail(e.target.value)} required /></div>
                  <div className="space-y-2"><Label>Role</Label>
                    <Select value={newUserRole} onValueChange={(v: any) => setNewUserRole(v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="ADMIN">Admin</SelectItem><SelectItem value="PM">PM</SelectItem><SelectItem value="USER">User</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Password</Label><Input type="password" value={newUserPassword} onChange={e=>setNewUserPassword(e.target.value)} required minLength={6} /></div>
                </form>
                <DialogFooter><Button data-testid="save-user-button" type="submit" form="create-user-form" loading={createBusy} disabled={createBusy}>Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </PageHeader>
          {uLoading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div> : (
            <DataTable columns={userColumns} data={userList} keyExtractor={(u: any) => u.id} emptyState={<EmptyState icon={Users} title="No users" description="Add your first user." />} />
          )}
        </TabsContent>
        <TabsContent value="permissions">
          <PageHeader title="" description="">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                <Lock className="h-5 w-5" />
              </div>
              <Button data-testid="seed-permissions-button" variant="outline" size="sm" onClick={handleSeedPermissions}><Plus className="mr-2 h-4 w-4" /> Seed Default Permissions</Button>
            </div>
          </PageHeader>
          <PermissionsMatrix />
        </TabsContent>
        <TabsContent value="security">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Settings className="h-4 w-4 text-[var(--color-primary)]" /> Security Settings</CardTitle></CardHeader>
            <CardContent>
              {configLoading ? <Skeleton className="h-8 w-full" /> : (
                <div className="flex items-center justify-between rounded-lg border border-[var(--color-border)] p-4">
                  <div>
                    <div className="text-sm font-medium">Enable public registration</div>
                    <div className="text-xs text-[var(--color-muted-foreground)]">Allow new users to register accounts publicly.</div>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      data-testid="toggle-registration-switch"
                      type="checkbox"
                      className="peer sr-only"
                      checked={registrationEnabled}
                      onChange={(e) => handleToggleRegistration(e.target.checked)}
                      disabled={configSaving}
                    />
                    <div className="h-6 w-11 rounded-full bg-[var(--color-slate-200)] peer-checked:bg-[var(--color-primary)] after:absolute after:left-[2px] after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-5" />
                  </label>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="audit">
          {lLoading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /></div> : (
            <DataTable
              columns={[
                { key: 'action', header: 'Action' },
                { key: 'target', header: 'Target' },
                { key: 'time', header: 'Time', render: (log: any) => <span className="text-xs text-[var(--color-muted-foreground)]">{new Date(log.createdAt).toLocaleString()}</span> },
              ]}
              data={logs}
              keyExtractor={(log) => log.id}
              emptyState={<EmptyState icon={ShieldCheck} title="No audit logs" description="System activity will appear here." />}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Edit user dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle><DialogDescription>Update user details.</DialogDescription></DialogHeader>
          <form id="edit-user-form" onSubmit={handleEditUser} className="space-y-4 py-2">
            {editError && <Badge variant="destructive">{editError}</Badge>}
            <div className="space-y-2"><Label>Name</Label><Input value={editName} onChange={e=>setEditName(e.target.value)} required /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={editEmail} onChange={e=>setEditEmail(e.target.value)} required /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={editRole} onValueChange={(v: any) => setEditRole(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ADMIN">Admin</SelectItem><SelectItem value="PM">PM</SelectItem><SelectItem value="USER">User</SelectItem></SelectContent>
              </Select>
            </div>
          </form>
          <DialogFooter><Button data-testid="save-edit-user-button" type="submit" form="edit-user-form" loading={editBusy} disabled={editBusy}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>Set a new password for {resetUser?.name}.</DialogDescription></DialogHeader>
          <form id="reset-password-form" onSubmit={handleResetPassword} className="space-y-4 py-2">
            <div className="space-y-2"><Label>New Password</Label><Input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} required minLength={6} /></div>
          </form>
          <DialogFooter><Button data-testid="confirm-reset-password-button" type="submit" form="reset-password-form" loading={resetBusy} disabled={resetBusy}>Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
